/**
 * Exposure Apparel — print rulebook (single source of truth for print size bands).
 *
 * Used by the design tool (assets/ea-design-tool.js), the print location picker
 * (assets/custom-main-product.js) and the cart pop-up (assets/ea-print-placements.js),
 * so the same artwork at the same size always gets the same price band.
 *
 * DTF / digital print is priced by the size the artwork is printed at (its bounding box):
 *   Small  fits within 10 × 10 cm   (left chest, sleeve, back neck)
 *   Medium fits within 20 × 20 cm   (centre chest, smaller upper-back prints)
 *   Large  fits within 30 × 30 cm   (most full fronts and backs)
 *   XL     anything bigger, up to the garment's print area
 * Each band is a variant of the "Customisation" product (Print Type × Size).
 *
 * Embroidery keeps two bands (Small fits within 12 × 12 cm, otherwise Large) until the
 * embroidery supplier and stitch-count pricing are decided.
 */
(function () {
  'use strict';

  const DTF_BANDS = [
    { size: 'Small', maxCm: 10 },
    { size: 'Medium', maxCm: 20 },
    { size: 'Large', maxCm: 30 },
    { size: 'XL', maxCm: Infinity },
  ];
  const EMBROIDERY_SMALL_MAX_CM = 12;
  const TOLERANCE_CM = 0.05; // rounding slack so a 10.0 cm print is still Small

  // Default print size for each set location (width × height in cm, adult sizes).
  // The location picker charges the band of this default size; the design tool charges
  // the band of the size the customer actually places.
  const LOCATION_DEFAULTS = {
    'Front Left Chest': { w: 9, h: 9 },
    'Front Right Chest': { w: 9, h: 9 },
    'Center Front': { w: 10, h: 10 },
    'Full Front': { w: 28, h: 28 },
    'Over Pocket': { w: 26, h: 12 },
    'Lower Front': { w: 28, h: 14 },
    'Lower Left': { w: 12, h: 12 },
    'Lower Right': { w: 12, h: 12 },
    'Vertical Left': { w: 7, h: 28 },
    'Vertical Right': { w: 7, h: 28 },
    'Top Back': { w: 28, h: 10 },
    'Center Back': { w: 30, h: 30 },
    'Lower Back': { w: 28, h: 14 },
    'Vertical Back L': { w: 7, h: 28 },
    'Vertical Back R': { w: 7, h: 28 },
    'Left Sleeve': { w: 8, h: 8 },
    'Right Sleeve': { w: 8, h: 8 },
    'Left Side': { w: 8, h: 8 },
    'Right Side': { w: 8, h: 8 },
  };

  const SIZES = DTF_BANDS.map((b) => b.size);

  function bandFor(method, widthCm, heightCm) {
    const longest = Math.max(Number(widthCm) || 0, Number(heightCm) || 0);
    if (method === 'Embroidery') {
      return longest <= EMBROIDERY_SMALL_MAX_CM + TOLERANCE_CM ? 'Small' : 'Large';
    }
    const band = DTF_BANDS.find((b) => longest <= b.maxCm + TOLERANCE_CM);
    return band ? band.size : 'XL';
  }

  function locationSize(locationValue, method) {
    const d = LOCATION_DEFAULTS[locationValue];
    if (!d) return 'Small';
    return bandFor(method || 'Digital Print', d.w, d.h);
  }

  // Human-readable rule for help text, e.g. "Small fits within 10 × 10 cm".
  function describeBands(method) {
    if (method === 'Embroidery') {
      return [`Small fits within ${EMBROIDERY_SMALL_MAX_CM} × ${EMBROIDERY_SMALL_MAX_CM} cm`, 'Large is anything bigger'];
    }
    return DTF_BANDS.map((b) =>
      b.maxCm === Infinity ? `${b.size} is anything bigger` : `${b.size} fits within ${b.maxCm} × ${b.maxCm} cm`
    );
  }

  window.EA_PRINT_RULES = {
    SIZES,
    DTF_BANDS,
    EMBROIDERY_SMALL_MAX_CM,
    LOCATION_DEFAULTS,
    bandFor,
    locationSize,
    describeBands,
  };
})();
